import { useEffect, useMemo, useRef } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useMetroMapDispatch, useMetroMapStore } from './useMetroMapStore'
import { useSchema } from '../components/useSchema'
import { buildSlugIndex } from './slugIndex'
import { parseRoutePairSlug } from './routePairSlug'
import { resolveStationBySlug } from '../interaction/stationResolver'
import { stationSnapshotFromSchema } from './stationSnapshotFromSchema'
import { PARAM_SUGGESTER } from './useSuggesterPanel'

const PARAM_FROM = 'from'
const PARAM_TO = 'to'

/**
 * Presence-only React component (returns `null`) that bridges the
 * `routeSelection` slice of the MetroMap store with the browser URL's
 * `?from=<slug>&to=<slug>` query params, so an in-progress origin/
 * destination pair survives reload AND can be shared/deep-linked.
 *
 * Mount point: must live inside `<SchemaProvider>` →
 * `<MetroMapStoreProvider>` (Task 10 wires this in `AppShell`). Both
 * providers are required — the component reads schema (slug ↔ id) and
 * the store dispatch/subscribe handles. It also requires a React
 * Router context (`useSearchParams`).
 *
 * Hydration order (URL > localStorage > empty):
 *   1. THIS component runs first inside `AppShell` and populates
 *      `routeSelection` synchronously from `?from=&to=` if present.
 *   2. `RouteSelectionLocalStorageSync` (Task 8) runs second and
 *      hydrates from localStorage ONLY if the store is still empty —
 *      so URL state always wins on cold start.
 *   3. If neither URL nor localStorage carries selection, the store
 *      keeps its initial empty state.
 *
 * Mirror direction: subscribes to the store and writes
 * `?from=<slug>&to=<slug>` on every mutation, using `replace: true` so
 * route selection toggles do NOT pollute browser history (back-button
 * skips the round-trip and returns to whatever page the user came
 * from). Normally this is query-only (path untouched). The mirror also owns
 * the built-route surface (TASK-275 canonical deep link): when the route
 * becomes COMPLETE (both From and To set) it moves the URL to
 * `/<locale>/route?from=&to=` (dropping any `/stations/<slug>` mixed-URL
 * path + `?station=&line=` — a built route supersedes an inspected station);
 * when the route becomes incomplete while on that surface it returns to the
 * locale index.
 *
 * Cluster disambiguation: this component uses the slug-only resolver
 * (`resolveStationBySlug`); for a multi-line cluster (e.g. Baker Street
 * carries Bakerloo + Jubilee + Circle + Hammersmith & City + Metropolitan)
 * the resolver silently picks the cluster's `primaryLineId` record.
 * Adding `?from-line=` / `?to-line=` for exact disambiguation is future
 * work (see plan Section 8.1, "Cluster disambiguation").
 *
 * Returns `null` because it has no DOM presence — this is a pure
 * side-effect component (the React equivalent of a Swift system
 * service or Android `lifecycleScope` job).
 */
export function RouteSelectionUrlSync(): null {
  const store = useMetroMapStore()
  const dispatch = useMetroMapDispatch()
  const schema = useSchema()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { locale, pair } = useParams<{ locale?: string; pair?: string }>()
  // Frozen at mount for the same reason `initialSearchParamsRef` is: the
  // hydration effect must read the URL the user arrived on, not a later one.
  const initialPairRef = useRef(pair)
  // Keep the live pathname / search / locale in refs so the store
  // subscription (whose closure is created once and would otherwise capture
  // stale values) can read the CURRENT ones when it fires — needed to decide
  // whether a completed route must move to the canonical `/<locale>/route`
  // surface, or a cleared route must return to the locale index.
  const pathnameRef = useRef(location.pathname)
  const searchRef = useRef(location.search)
  const localeRef = useRef(locale)
  useEffect(() => {
    pathnameRef.current = location.pathname
    searchRef.current = location.search
    localeRef.current = locale
  }, [location.pathname, location.search, locale])
  // The hydrate effect reads from `searchParams` once; capturing the
  // first value via a ref keeps the effect's dep-array small and
  // avoids re-running it when our own mirror writes mutate the URL.
  // (The `hydrationStarted` latch is the source of truth, but pulling
  // `searchParams` out of deps also avoids spurious schedules.)
  const initialSearchParamsRef = useRef(searchParams)
  // StrictMode latch — flipped synchronously at the top of the hydrate
  // effect to prevent the second dev-mode invocation from re-running
  // the hydration logic (effect bodies fire twice in StrictMode, refs
  // are preserved across the re-runs of the same component instance).
  const hydrationStarted = useRef(false)
  // "Hydration is finished, mirror writes are now safe" gate. Flipped
  // AFTER all hydration dispatches have completed so the mirror handler
  // can ignore those dispatches (otherwise the post-hydrate dispatch
  // round-trips back to the URL with the same slug we just read).
  const hydrated = useRef(false)

  // Slug ↔ id index. Memoised on the schema reference so a remount
  // doesn't rebuild N maps; `useSchema` returns a stable reference once
  // the fetch resolves.
  const slugIndex = useMemo(() => (schema === null ? null : buildSlugIndex(schema)), [schema])

  // Hydrate ONCE on mount when the schema is loaded. The mirror effect
  // must NOT see hydration's own dispatches, so we flip
  // `hydrated.current` only after the dispatches have settled.
  useEffect(() => {
    if (hydrationStarted.current || schema === null) return
    hydrationStarted.current = true

    // Read from the React-Router-owned `searchParams` snapshot taken at
    // mount, NOT from `window.location.search` — `<MemoryRouter>` (used
    // in tests and shareable-link tooling) keeps history in JS memory
    // and never mutates `window.location`. The ref capture is the
    // hydration-latch counterpart in space: stable URL frozen at first
    // render, freed from re-running when our own mirror writes change
    // the live `searchParams`.
    const urlParams = initialSearchParamsRef.current
    // A curated journey page (`/routes/<from>-to-<to>`) carries the pair in
    // the PATH, not the query. Those 464 URLs are what the sitemap publishes
    // and what search traffic actually lands on, so they hydrate the same
    // selection the query form does — deliberately without rewriting the URL,
    // since the indexed `/routes/...` form is the canonical one.
    const pathPair =
      slugIndex === null ? null : parseRoutePairSlug(initialPairRef.current, slugIndex.idBySlug)
    const fromSlug = pathPair?.fromSlug ?? urlParams.get(PARAM_FROM)
    const toSlug = pathPair?.toSlug ?? urlParams.get(PARAM_TO)

    // Neither param is present — nothing to read, but still flip
    // `hydrated` so the mirror effect can begin writing on the next
    // user mutation.
    if (fromSlug === null && toSlug === null) {
      hydrated.current = true
      return
    }

    // Defence in depth — if the store has already been populated by
    // another source (e.g. a future `<UrlToStoreSync>` cousin or a
    // direct dispatch), do not overwrite. Currently no such cousin
    // exists for `routeSelection`, but the guard is cheap and matches
    // `RouteSelectionLocalStorageSync`'s shape.
    const current = store.getState().routeSelection
    if (current.fromStation !== null || current.toStation !== null) {
      hydrated.current = true
      return
    }

    if (fromSlug !== null) {
      const resolved = resolveStationBySlug(schema, fromSlug)
      if (resolved !== null) {
        dispatch({
          kind: 'RouteFromSelected',
          station: stationSnapshotFromSchema(resolved),
        })
      }
    }
    if (toSlug !== null) {
      const resolved = resolveStationBySlug(schema, toSlug)
      if (resolved !== null) {
        dispatch({
          kind: 'RouteToSelected',
          station: stationSnapshotFromSchema(resolved),
        })
      }
    }
    // Open the mirror gate AFTER the hydration dispatches have
    // completed. Any subsequent dispatch is a real user mutation worth
    // mirroring; hydration's own dispatches must not round-trip back
    // to the URL.
    hydrated.current = true
    // `slugIndex` is memoised from `schema`, which is already a dependency, so
    // it changes identity in lockstep and adds no extra runs — it is listed
    // because the curated-journey pair is resolved through it.
  }, [schema, slugIndex, store, dispatch])

  // Mirror store → URL on every mutation. Subscribes only — the initial
  // render does NOT write (subscribe handlers fire on dispatch, not on
  // subscribe-time, so no spurious URL update with a freshly-empty
  // selection on cold mount). Unsubscribes on unmount. `dispatch` is
  // intentionally NOT in the dep-array — the effect doesn't reference it.
  useEffect(() => {
    if (slugIndex === null) return
    return store.subscribe(() => {
      // Suppress writes that originate from hydration itself —
      // otherwise a cold-start with `?from=baker-street` would write
      // the same slug back through the mirror (one redundant URL
      // update per cold mount).
      if (!hydrated.current) return
      const sel = store.getState().routeSelection

      // Compute the next `?from=&to=` query from the current selection.
      const applyRouteParams = (base: URLSearchParams): URLSearchParams => {
        const next = new URLSearchParams(base)
        if (sel.fromStation === null) {
          next.delete(PARAM_FROM)
        } else {
          const slug = slugIndex.slugById.get(sel.fromStation.id)
          if (slug === undefined) next.delete(PARAM_FROM)
          else next.set(PARAM_FROM, slug)
        }
        if (sel.toStation === null) {
          next.delete(PARAM_TO)
        } else {
          const slug = slugIndex.slugById.get(sel.toStation.id)
          if (slug === undefined) next.delete(PARAM_TO)
          else next.set(PARAM_TO, slug)
        }
        return next
      }

      // Route ⇄ station mutual exclusivity + canonical route surface
      // (bug-fix / TASK-275 2026-07-06). The mirror is the sole writer of the
      // route query AND the one that fires on Routing request/result churn,
      // so it owns WHERE a built route lives:
      //   • COMPLETE route (both From and To resolvable to slugs) → the
      //     canonical `/<locale>/route?from=&to=` surface. Moving here from
      //     anywhere (index or a `/stations/<slug>` mixed URL) drops the
      //     station-only params (`?station=&line=`) — a built route supersedes
      //     an inspected station.
      //   • INCOMPLETE route while sitting ON `/<locale>/route` → back to the
      //     locale index (the route surface is only for a complete pair).
      //   • Anything else → query-only (`setSearchParams`, path untouched).
      const prefix = localeRef.current !== undefined ? `/${localeRef.current}` : ''
      const indexPath = prefix === '' ? '/' : prefix
      const routePath = `${prefix}/route`
      const path = pathnameRef.current
      const onRoutePath = path === routePath || path === '/route'

      // Mid-pop guard (TASK-699 back-button trap): during a history pop
      // to a surface OUTSIDE the map (the station-page template
      // `/station/<id>`, legal pages) the BROWSER url has already moved
      // while the router state — and therefore `pathnameRef` and the
      // setSearchParams base — still hold the map location. A write here
      // re-canonicalises the OLD map URL over the entry the user just
      // popped to, so Back appeared to reload the map instead of
      // returning. `window.location` is the truth mid-transition; this
      // mirror only owns URLs on the map's own surfaces (locale index,
      // /stations/*, /route, /routes/*, /lines/*).
      const winPath = window.location.pathname
      const onMapSurface =
        winPath === '/' ||
        winPath === indexPath ||
        winPath === `${indexPath}/` ||
        winPath.startsWith(`${prefix}/stations/`) ||
        winPath.startsWith('/stations/') ||
        winPath === routePath ||
        winPath === '/route' ||
        // Curated journey pages stay in scope: the mirror must still be
        // able to move the user onto `/route` once they change an
        // endpoint (the branch below decides). Excluding them here would
        // have silently frozen that navigation.
        winPath.startsWith(`${prefix}/routes/`) ||
        winPath.startsWith('/routes/') ||
        // The line view: a route completed from it moves to `/route`.
        winPath.startsWith(`${prefix}/lines/`)
      if (!onMapSurface) return

      // A curated journey page (`/<locale>/routes/<from>-to-<to>`) is an
      // EQUALLY canonical surface for a complete pair — it is the form the
      // sitemap publishes and the one Google has indexed. Rewriting it to
      // `/route?from=&to=` would move the canonical away from the URL that
      // earns the traffic, so the mirror leaves the path alone while the pair
      // it encodes still matches the selection.
      const onCuratedPairPath = initialPairRef.current !== undefined && path.includes('/routes/')
      const fromSlug =
        sel.fromStation === null ? undefined : slugIndex.slugById.get(sel.fromStation.id)
      const toSlug = sel.toStation === null ? undefined : slugIndex.slugById.get(sel.toStation.id)
      const routeComplete = fromSlug !== undefined && toSlug !== undefined

      const navigateWithRouteQuery = (pathname: string): void => {
        const next = applyRouteParams(new URLSearchParams(searchRef.current))
        next.delete('station')
        next.delete('line')
        // A built route supersedes an open Suggester: drop `?suggester=`
        // so the picker closes and RouteDetails shows on the canonical
        // surface (bug-fix 2026-07 — a completed route left `?suggester=from`
        // behind, re-opening the picker over the route, e.g. deep-link
        // `/route?from=&suggester=from&to=`). The interactive select path
        // (`useSuggesterPanel.onStationSelected`) already drops the param
        // atomically; this closes the mirror-owned route-surface path too.
        next.delete(PARAM_SUGGESTER)
        const query = next.toString()
        navigate({ pathname, search: query === '' ? '' : `?${query}` }, { replace: true })
      }

      if (routeComplete) {
        // Sitting on the curated journey page this very pair names: the path
        // already IS the canonical surface, so write nothing. Once the user
        // changes either endpoint the pair no longer matches and the branch
        // below moves them onto `/route` as usual.
        const pairMatchesSelection =
          onCuratedPairPath && initialPairRef.current === `${fromSlug}-to-${toSlug}`
        if (pairMatchesSelection) return
        // Canonical surface: move to `/<locale>/route` unless already there.
        if (!onRoutePath) {
          navigateWithRouteQuery(routePath)
          return
        }
        // Already on `/route` (e.g. a deep-linked complete pair, or the
        // routing controller emitting results while sitting here): still
        // drop `?suggester=` so a completed route never keeps the picker
        // open over RouteDetails.
        setSearchParams(
          (prev) => {
            const next = applyRouteParams(prev)
            next.delete(PARAM_SUGGESTER)
            return next
          },
          { replace: true },
        )
        return
      }

      // Incomplete route. Leaving the canonical `/route` surface returns to
      // the locale index; otherwise stay put (query-only).
      if (onRoutePath) {
        navigateWithRouteQuery(indexPath)
        return
      }
      setSearchParams((prev) => applyRouteParams(prev), { replace: true })
    })
  }, [slugIndex, store, setSearchParams, navigate])

  return null
}
